import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProfile } from '../contexts/ProfileContext';
import { useStreamAuth } from '../hooks/useStreamAuth';
import LoadingSpinner from '../components/auth/LoadingSpinner';
import DeleteProfileModal from '../components/DeleteProfileModal';


const ProfileSelectionPage = () => {
  const { loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  const { isAuthenticated, user } = useStreamAuth();
  const {
    profiles,
    currentProfile,
    remainingProfiles,
    isLoading,
    error,
    hasLoadedProfiles,
    loadProfiles,
    createProfile,
    updateProfile,
    deleteProfile,
    regenerateAvatar,
    setActiveProfile,
    canCreateProfile,
  } = useProfile();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [editingProfile, setEditingProfile] = useState(null);
  const [editProfileName, setEditProfileName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, profile: null });

  // Helper function to get profile avatar with Google profile for main account only
  const getProfileAvatar = profile => {
    // Only use Google profile picture for the main account holder (Owner profile)
    // if (profile?.profile_name === 'Owner' && user?.picture) {
    //   return user.picture;
    // }
    // For all other profiles, use ElectricNOW profile avatar
    if (profile?.profile_thumb) {
      return profile.profile_thumb;
    }
    return null;
  };

  // Load profiles when component mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated && user && !hasLoadedProfiles) {
      loadProfiles();
    }
  }, [isAuthenticated, user, hasLoadedProfiles, loadProfiles]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect();
    }
  }, [isAuthenticated, navigate]);

  // Handle profile selection
  const handleProfileSelect = async profile => {
    const success = await setActiveProfile(profile);
    if (success) {
      // Navigate to home page after successful profile selection
      const params = new URLSearchParams(location.search);
      const redirect = params.get('after_selection');
      if (redirect?.length) {
        return navigate(redirect);
      }
      navigate('/');
    }
  };

  // Handle create profile
  const [createError, setCreateError] = useState(null);

  const handleCreateProfile = async e => {
    e.preventDefault();
    if (!newProfileName.trim()) return;

    setIsCreating(true);
    setCreateError(null);

    const result = await createProfile(newProfileName.trim());

    if (result.success) {
      setNewProfileName('');
      setShowCreateForm(false);
      setCreateError(null);
    } else {
      setCreateError(result.error);
    }
    setIsCreating(false);
  };

  // Handle edit profile
  const handleEditProfile = async e => {
    e.preventDefault();
    if (!editProfileName.trim() || !editingProfile) return;

    const success = await updateProfile(editingProfile._id, editProfileName.trim());

    if (success) {
      setEditingProfile(null);
      setEditProfileName('');
    }
  };

  // Handle delete profile
  const handleDeleteProfile = async profile => {
    setDeleteModal({ isOpen: true, profile });
  };

  // Confirm delete profile
  const confirmDeleteProfile = async () => {
    if (deleteModal.profile) {
      await deleteProfile(deleteModal.profile._id);
      setDeleteModal({ isOpen: false, profile: null });
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, profile: null });
  };

  // Handle regenerate avatar
  const handleRegenerateAvatar = async profile => {
    await regenerateAvatar(profile._id);
  };

  // Start editing a profile
  const startEditing = profile => {
    setEditingProfile(profile);
    setEditProfileName(profile.profile_name);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingProfile(null);
    setEditProfileName('');
  };

  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  if (isLoading && !hasLoadedProfiles) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen text-white bg-page">
        <div className="text-center">
          <div className="mb-4 text-6xl">⚠️</div>
          <h2 className="mb-4 text-2xl font-bold">Error Loading Profiles</h2>
          <p className="mb-6 text-white/70">{error}</p>
          <button
            onClick={() => loadProfiles(true)}
            className="px-6 py-2 text-white transition-colors rounded-lg bg-accent hover:bg-accent/80"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>

        <div className="relative min-h-screen text-white overflow-hidden">
          {/* Background Image */}
          <div className="fixed inset-0 z-0">
            <img
              src="/assets/images/electric-now-landing-hero.jpg"
              alt="ElectricNOW background"
              className="object-cover w-full h-full"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
          </div>
          
          {/* Content */}
          <div className="relative z-10 container px-6 py-12 mx-auto">
            {/* Header */}
            <div className="mb-12 text-center">
              <h1 className="mb-4 text-4xl font-bold">Who's watching?</h1>
              <p className="text-lg text-white/70">
                Select a profile to continue with your personalized experience
              </p>
            </div>

            {/* Profiles Grid */}
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 gap-6 mb-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {profiles.map(profile => (
                  <div key={profile._id} className="text-center group">
                    {editingProfile && editingProfile._id === profile._id ? (
                      // Edit Form
                      <div className="p-4 rounded-lg bg-secondary">
                        <form onSubmit={handleEditProfile}>
                          <input
                            type="text"
                            value={editProfileName}
                            onChange={e => setEditProfileName(e.target.value)}
                            className="w-full px-3 py-2 mb-3 text-sm text-white bg-gray-700 rounded"
                            placeholder="Profile name"
                            maxLength={20}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              type="submit"
                              disabled={!editProfileName.trim() || isLoading}
                              className="flex-1 px-2 py-1 text-xs text-white transition-colors rounded bg-accent hover:bg-accent/80 disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditing}
                              className="flex-1 px-2 py-1 text-xs text-white transition-colors bg-gray-600 rounded hover:bg-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    ) : (
                      // Profile Card
                      <>
                        <div
                          onClick={() => handleProfileSelect(profile)}
                          className="relative transition-transform transform cursor-pointer hover:scale-105"
                        >
                          <div className="w-24 h-24 mx-auto mb-3 overflow-hidden transition-colors bg-gray-700 border-2 border-transparent rounded-lg hover:border-accent">
                            {getProfileAvatar(profile) ? (
                              <img
                                src={getProfileAvatar(profile)}
                                alt={profile.profile_name}
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              <div className="flex items-center justify-center w-full h-full text-2xl font-bold text-white/70">
                                {profile.profile_name.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <h3 className="text-sm font-medium text-white transition-colors group-hover:text-accent">
                            {profile.profile_name}
                          </h3>
                        </div>

                        {/* Profile Actions */}
                        <div className="mt-2 transition-opacity opacity-0 group-hover:opacity-100">
                          <div className="flex justify-center gap-1">
                            <button
                              onClick={() => startEditing(profile)}
                              className="text-xs transition-colors text-white/70 hover:text-white"
                              title="Edit profile"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleRegenerateAvatar(profile)}
                              className="text-xs transition-colors text-white/70 hover:text-white"
                              title="Regenerate avatar"
                              disabled={isLoading}
                            >
                              🔄
                            </button>
                            {profiles.length > 1 && (
                              <button
                                onClick={() => handleDeleteProfile(profile)}
                                className="text-xs text-red-400 transition-colors hover:text-red-300"
                                title="Delete profile"
                                disabled={isLoading}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* Add Profile Button */}
                {canCreateProfile() && (
                  <div className="text-center">
                    {showCreateForm ? (
                      // Create Form
                      <div className="p-4 rounded-lg bg-secondary">
                        <form onSubmit={handleCreateProfile}>
                          <input
                            type="text"
                            value={newProfileName}
                            onChange={e => setNewProfileName(e.target.value)}
                            className="w-full px-3 py-2 mb-3 text-sm text-white bg-gray-700 rounded"
                            placeholder="Profile name"
                            maxLength={20}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              type="submit"
                              disabled={!newProfileName.trim() || isCreating}
                              className="flex-1 px-2 py-1 text-xs text-white transition-colors rounded bg-accent hover:bg-accent/80 disabled:opacity-50"
                            >
                              {isCreating ? 'Creating...' : 'Create'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowCreateForm(false);
                                setNewProfileName('');
                              }}
                              className="flex-1 px-2 py-1 text-xs text-white transition-colors bg-gray-600 rounded hover:bg-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    ) : (
                      // Add Button
                      <div
                        onClick={() => setShowCreateForm(true)}
                        className="transition-transform transform cursor-pointer hover:scale-105"
                      >
                        <div className="flex items-center justify-center w-24 h-24 mx-auto mb-3 transition-colors bg-gray-700 border-2 border-gray-500 border-dashed rounded-lg hover:border-accent">
                          <span className="text-3xl text-gray-400 transition-colors hover:text-accent">
                            +
                          </span>
                        </div>
                        <h3 className="text-sm font-medium text-gray-400 transition-colors hover:text-accent">
                          Add Profile
                        </h3>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Profile Limit Info */}
              {remainingProfiles === 0 && (
                <div className="text-sm text-center text-white/50">
                  Maximum number of profiles reached
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-12 text-center">
              <p className="text-sm text-white/50">
                Each profile has its own watchlist and viewing history
              </p>
            </div>
          </div>
        </div>

      {/* Delete Profile Modal */}
      <DeleteProfileModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteProfile}
        profileName={deleteModal.profile?.profile_name || ''}
      />
    </>
  );
};

export default ProfileSelectionPage;
